/** Divide-first geometry helpers — see docs/data-collection/ROAD_LANE_DIRECTION_POLICY.md §2.11 */

export type DividedCarriagewayGuideSide = 'a' | 'b' | 'divide';

const EARTH_RADIUS_M = 6371008.8;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

function toDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}

function bearingDeg(lng1: number, lat1: number, lng2: number, lat2: number): number {
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δλ = toRad(lng2 - lng1);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

function destinationPoint(
  lng: number,
  lat: number,
  bearing: number,
  distanceM: number,
): [number, number] {
  const δ = distanceM / EARTH_RADIUS_M;
  const θ = toRad(bearing);
  const φ1 = toRad(lat);
  const λ1 = toRad(lng);
  const φ2 = Math.asin(
    Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ),
  );
  const λ2 =
    λ1 +
    Math.atan2(
      Math.sin(θ) * Math.sin(δ) * Math.cos(φ1),
      Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2),
    );
  return [toDeg(λ2), toDeg(φ2)];
}

/** Positive offset = left of digitization direction (matches PostGIS ST_OffsetCurve sign). */
export function offsetLineStringMeters(
  line: GeoJSON.LineString,
  offsetM: number,
): GeoJSON.LineString | null {
  const coords = line.coordinates;
  if (coords.length < 2) return null;
  if (offsetM === 0) return line;

  const out: [number, number][] = [];
  for (let i = 0; i < coords.length; i += 1) {
    const [lng, lat] = coords[i] as [number, number];
    let azimuth: number;
    if (i === 0) {
      const [lng2, lat2] = coords[i + 1] as [number, number];
      azimuth = bearingDeg(lng, lat, lng2, lat2);
    } else if (i === coords.length - 1) {
      const [lng0, lat0] = coords[i - 1] as [number, number];
      azimuth = bearingDeg(lng0, lat0, lng, lat);
    } else {
      const [lng0, lat0] = coords[i - 1] as [number, number];
      const [lng2, lat2] = coords[i + 1] as [number, number];
      const b1 = bearingDeg(lng0, lat0, lng, lat);
      const b2 = bearingDeg(lng, lat, lng2, lat2);
      azimuth = (b1 + b2) / 2;
      if (Math.abs(b2 - b1) > 180) azimuth = (azimuth + 180) % 360;
    }
    const perp = offsetM >= 0 ? (azimuth + 90) % 360 : (azimuth - 90 + 360) % 360;
    out.push(destinationPoint(lng, lat, perp, Math.abs(offsetM)));
  }
  return { type: 'LineString', coordinates: out };
}

/** Derive carriageway centre lines from a pavement-seam divide trace and full paved widths (m). */
export function deriveCarriagewaysFromDivide(
  divide: GeoJSON.LineString,
  widthA: number,
  widthB: number,
): { a: GeoJSON.LineString; b: GeoJSON.LineString } | null {
  if (widthA <= 0 || widthB <= 0 || divide.coordinates.length < 2) return null;
  const a = offsetLineStringMeters(divide, widthA / 2);
  const b = offsetLineStringMeters(divide, -(widthB / 2));
  if (!a || !b) return null;
  return { a, b };
}
